// Deployment logs panel for the v2 UI, shown inside the DeploymentProgress
// dialog's "Logs" tab.
//
// Renders the reassembled per-task / per-step logs from useDeploymentLogs as
// collapsible task sections with a status chip and a monospace, pre-wrapped
// log block per step. The deployer emits slog JSON, so each line is parsed by
// lib/logFormat and rendered as readable text colorized by level, with a
// toolbar toggle that drops back to the raw JSON. Tails live deploys: while the
// record is in flight the hook re-fetches every few seconds, and this view
// auto-scrolls to the bottom only when the user is already there (so scrolling
// up to read isn't yanked back). Loading / empty / error / "no logs available"
// / "engine publishes no logs here" states are all handled, as is a task whose
// output was trimmed to its tail (with the presigned full-log download).
//
// A machine-readable JSON dump line (lib/logFormat `isJsonDump` — e.g. the
// `tofu show -json` plan the deployer once teed into its logs, which carries
// plaintext sensitive values) never renders: the formatted view, the raw view
// and the copy all show a muted size placeholder instead. There is deliberately
// no expand control; the "Download full log" link is the only way to the bytes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  DataObject as DataObjectIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { getColors, PaletteMode, rounded } from '../theme/tokens';
import { alpha, lighten, statusHues, tagStyle } from '../theme/statusTokens';
import { useDeploymentLogs } from '../hooks/useDeploymentLogs';
import { logsToPlainText } from '../lib/logs';
import { formatLogText, hiddenPlaceholder, LogAttr, redactJsonDumps } from '../lib/logFormat';
import { AnsiColor, AnsiSegment, stripAnsi } from '../lib/ansi';
import { planUpdateHue } from '../lib/planColorize';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/** Indent for a step-completed block's attr lines, past the time column. */
const BLOCK_INDENT = '         ';

/** Map a backend task status onto a v2 status hue for the chip. */
const taskHue = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'succeeded':
      return statusHues.success;
    case 'failed':
      return statusHues.failed;
    case 'running':
      return statusHues.inprogress;
    case 'pending':
      return statusHues.pending;
    default:
      return statusHues.unknown;
  }
};

/** Text colors for one log line, by slog level. */
const levelStyle = (
  level: string,
  mode: PaletteMode
): { levelColor: string; msgColor: string; bold: boolean } => {
  const c = getColors(mode);
  const dark = mode === 'dark';
  switch (level.toUpperCase()) {
    case 'ERROR': {
      const hue = dark ? lighten(statusHues.failed, 0.3) : statusHues.failed;
      return { levelColor: hue, msgColor: hue, bold: true };
    }
    case 'WARN': {
      const hue = dark ? lighten(planUpdateHue, 0.3) : planUpdateHue;
      return { levelColor: hue, msgColor: c.ink, bold: true };
    }
    case 'INFO':
      return { levelColor: c.ink, msgColor: c.ink, bold: false };
    default:
      // DEBUG, and anything the deployer starts emitting that we don't know.
      return { levelColor: c.inkMuted, msgColor: c.inkMuted, bold: false };
  }
};

/** App-palette hue for an ANSI color, lifted in dark mode like the level colors. */
const ansiHue = (color: AnsiColor, mode: PaletteMode): string | undefined => {
  const c = getColors(mode);
  const base: Record<AnsiColor, string | undefined> = {
    red: statusHues.failed,
    green: statusHues.success,
    yellow: planUpdateHue,
    blue: statusHues.inprogress,
    magenta: '#9b51e0',
    cyan: '#0f8b8d',
    // Black/white are the terminal's own fg/bg — follow the theme's ink instead,
    // so neither vanishes against the opposite background.
    black: c.ink,
    white: c.ink,
  };
  const hue = base[color];
  return hue && mode === 'dark' && color !== 'black' && color !== 'white'
    ? lighten(hue, 0.3)
    : hue;
};

/** One tool-output line whose ANSI escapes became styled runs. */
const AnsiLine: React.FC<{ segments: AnsiSegment[]; mode: PaletteMode }> = ({ segments, mode }) => {
  if (segments.length === 0) return <div> </div>;
  return (
    <div>
      {segments.map((seg, i) => (
        <span
          key={i}
          style={{
            color: seg.color ? ansiHue(seg.color, mode) : undefined,
            fontWeight: seg.bold ? 600 : undefined,
            opacity: seg.dim ? 0.7 : undefined,
            fontStyle: seg.italic ? 'italic' : undefined,
            textDecoration: seg.underline ? 'underline' : undefined,
          }}
        >
          {seg.text}
        </span>
      ))}
    </div>
  );
};

const widestKey = (details: LogAttr[]): number =>
  details.reduce((max, d) => Math.max(max, d.key.length), 0);

interface StepLogBlockProps {
  log: string;
  /** The parent task's status hue, tinting the block background. */
  hue: string;
  mode: PaletteMode;
  /** Show the log exactly as the deployer emitted it, JSON and all. */
  raw: boolean;
}

// One step's log body. Memoized because a live poll replaces the whole payload
// every few seconds and only the growing step actually changes.
const StepLogBlock = React.memo(function StepLogBlock({
  log,
  hue,
  mode,
  raw,
}: StepLogBlockProps) {
  const c = getColors(mode);
  const lines = useMemo(() => formatLogText(log), [log]);
  // Only computed when the raw view is on; redaction walks the whole log.
  const rawText = useMemo(() => (raw ? redactJsonDumps(stripAnsi(log)) : ''), [raw, log]);

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        borderRadius: `${rounded.xs}px`,
        backgroundColor: alpha(hue, mode === 'dark' ? 0.06 : 0.04),
        fontFamily: MONO,
        fontSize: '0.75rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: c.ink,
      }}
    >
      {raw
        ? rawText.length
          ? rawText
          : ' '
        : lines.map((line, i) => {
            if (line.kind === 'hidden') {
              return (
                <div key={i} style={{ color: c.inkMuted, fontStyle: 'italic' }}>
                  {hiddenPlaceholder(line.chars)}
                </div>
              );
            }

            if (line.kind === 'raw') {
              if (line.segments) return <AnsiLine key={i} segments={line.segments} mode={mode} />;
              return <div key={i}>{line.text.length ? line.text : ' '}</div>;
            }

            if (line.kind === 'step') {
              const failed = line.outcome === 'error';
              const base = failed ? statusHues.failed : statusHues.success;
              const headlineHue = mode === 'dark' ? lighten(base, 0.3) : base;
              const width = widestKey(line.details) + 1;
              return (
                <React.Fragment key={i}>
                  <div>
                    <span style={{ color: c.inkMuted }}>{`${line.time} `}</span>
                    <span style={{ color: headlineHue, fontWeight: 600 }}>{line.headline}</span>
                  </div>
                  {line.details.map((d, j) => (
                    <div key={`${d.key}-${j}`}>
                      <span style={{ color: c.inkMuted }}>
                        {`${BLOCK_INDENT}${`${d.key}:`.padEnd(width)} `}
                      </span>
                      <span style={{ color: c.ink }}>{d.value}</span>
                    </div>
                  ))}
                </React.Fragment>
              );
            }

            const { levelColor, msgColor, bold } = levelStyle(line.level, mode);
            const weight = bold ? 600 : 400;
            return (
              <div key={i}>
                <span style={{ color: c.inkMuted }}>{`${line.time} `}</span>
                <span style={{ color: levelColor, fontWeight: weight }}>
                  {`${line.level.padEnd(5)} `}
                </span>
                <span style={{ color: msgColor, fontWeight: weight }}>{line.msg}</span>
                {line.attrs.length > 0 && (
                  <span style={{ color: c.inkMuted }}>
                    {`  ${line.attrs.map(a => `${a.key}=${a.value}`).join(' ')}`}
                  </span>
                )}
              </div>
            );
          })}
    </Box>
  );
});

export interface DeploymentLogsViewProps {
  record: DeploymentRecord;
  /** Gate polling — only true while the Logs tab is the active view. */
  enabled?: boolean;
}

const DeploymentLogsView: React.FC<DeploymentLogsViewProps> = ({ record, enabled = true }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const { logs, loading, error, refresh } = useDeploymentLogs(record, enabled);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [rawView, setRawView] = useState(false);

  const toggle = useCallback((task: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(task)) next.delete(task);
      else next.add(task);
      return next;
    });
  }, []);

  const handleCopy = useCallback(async () => {
    if (!logs) return;
    try {
      await navigator.clipboard.writeText(logsToPlainText(logs.tasks));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }, [logs]);

  // Auto-scroll to the bottom on new content, but only if the user hasn't
  // scrolled up to read (otherwise leave their position alone).
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [logs]);

  // First load with nothing yet on screen.
  if (loading && !logs) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" sx={{ color: c.inkMuted }}>
          Loading logs…
        </Typography>
      </Stack>
    );
  }

  if (error && !logs) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: statusHues.failed }}>
          {error}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={refresh}
          startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          sx={{ textTransform: 'none', mt: 0.5, px: 0 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // The backend decided from the record that this deployment's engine publishes
  // nothing here — Step Functions, whose output lives in CodeBuild and the
  // execution history, or the torn-down Tekton. Nothing to fetch and nothing to
  // retry, so say it plainly rather than showing this as a failure.
  if (logs && logs.source === 'unsupported') {
    return (
      <Typography variant="body2" sx={{ color: c.inkMuted, py: 1 }}>
        {`This isn't an Argo deployment so there are no logs. It ran on ${
          logs.engine || 'another engine'
        }.`}
      </Typography>
    );
  }

  if (logs && logs.source === 'none') {
    return (
      <Typography variant="body2" sx={{ color: c.inkMuted, py: 1 }}>
        Logs are not available for this deployment.
      </Typography>
    );
  }

  const tasks = logs?.tasks ?? [];

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75 }}
      >
        <Typography variant="caption" sx={{ color: c.inkMuted }}>
          Live logs
          {logs && !logs.deploymentComplete ? ' · streaming' : ''}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={rawView ? 'Show formatted' : 'Show raw JSON'} arrow>
            <IconButton
              size="small"
              aria-label="Toggle raw JSON"
              aria-pressed={rawView}
              onClick={() => setRawView(v => !v)}
              sx={{ color: rawView ? c.primary : c.inkMuted }}
            >
              <DataObjectIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" aria-label="Refresh logs" onClick={refresh} sx={{ color: c.inkMuted }}>
              <RefreshIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Copied' : 'Copy all logs'} arrow>
            <span>
              <IconButton
                size="small"
                aria-label="Copy all logs"
                onClick={handleCopy}
                disabled={tasks.length === 0}
                sx={{ color: copied ? statusHues.success : c.inkMuted }}
              >
                {copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {tasks.length === 0 ? (
        <Typography variant="body2" sx={{ color: c.inkMuted, py: 1 }}>
          No log output yet.
        </Typography>
      ) : (
        <Box
          ref={scrollRef}
          onScroll={onScroll}
          sx={{
            maxHeight: '52vh',
            overflow: 'auto',
            borderRadius: `${rounded.sm}px`,
            border: `1px solid ${c.hairline}`,
            backgroundColor: c.canvasSoft,
          }}
        >
          <Stack divider={<Box sx={{ borderTop: `1px solid ${c.hairline}` }} />}>
            {tasks.map(task => {
              const isCollapsed = collapsed.has(task.task);
              const hue = taskHue(task.taskStatus);
              const chip = tagStyle(hue, mode);
              return (
                <Box key={task.task}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggle(task.task)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(task.task);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 0.85,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: c.hoverWash },
                      '&:focus-visible': { outline: `2px solid ${c.primary}`, outlineOffset: -2 },
                    }}
                  >
                    {isCollapsed ? (
                      <ExpandMoreIcon sx={{ fontSize: 18, color: c.inkMuted }} />
                    ) : (
                      <ExpandLessIcon sx={{ fontSize: 18, color: c.inkMuted }} />
                    )}
                    <Typography
                      sx={{ fontWeight: 600, fontSize: '0.85rem', color: c.ink, minWidth: 0 }}
                      noWrap
                    >
                      {task.task || 'task'}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.1,
                        borderRadius: '4px',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        backgroundColor: chip.bg,
                        color: chip.text,
                        border: `1px solid ${chip.border}`,
                        flexShrink: 0,
                      }}
                    >
                      {task.taskStatus || 'unknown'}
                    </Box>
                  </Box>

                  <Collapse in={!isCollapsed} unmountOnExit>
                    <Box sx={{ px: 1.25, pb: 1 }}>
                      {task.truncated && (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 0.5, flexWrap: 'wrap' }}
                        >
                          <Typography variant="caption" sx={{ color: c.inkMuted }}>
                            Output truncated — showing the tail
                          </Typography>
                          {task.downloadUrl && (
                            <Button
                              variant="text"
                              size="small"
                              href={task.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                              sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
                            >
                              Download full log (link valid 15 min)
                            </Button>
                          )}
                        </Stack>
                      )}
                      {task.steps.map((step, i) => (
                        <Box key={`${step.step}-${i}`} sx={{ mb: i < task.steps.length - 1 ? 1 : 0 }}>
                          {step.step && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: c.inkMuted,
                                fontWeight: 600,
                                display: 'block',
                                mb: 0.25,
                              }}
                            >
                              {step.step}
                            </Typography>
                          )}
                          <StepLogBlock
                            log={step.log}
                            hue={hue}
                            mode={mode}
                            raw={rawView}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default DeploymentLogsView;
